/**
 * Matchablauf, Portal, Respawn und Overtime
 * (Masterprompt Abschnitte 49, 69, 71, 86, 87, 88).
 *
 * Diese Werte sind VERBINDLICH.
 */

import type { MatchPhase } from '@dueline/shared';

/**
 * Lebenspunkte des Portals. Bei 0 endet das Match sofort.
 *
 * Von 20 auf 40 (Produktentscheidung vom 29.09.2026). Der Wert stammte aus dem
 * Masterprompt und gilt damit als verbindlich - geaendert wird er nur wegen
 * dieser ausdruecklichen Entscheidung.
 *
 * Grund: Seit die Wellen groesser sind, kommt deutlich mehr durch.
 *
 * Wichtig fuer spaeter - dieser Regler ist schwaecher, als er aussieht:
 * Gemessen ueber je 720 Matches ergaben 20 Leben 7,4 Minuten, 40 Leben 8,0 und
 * 90 Leben 9,0. Das 4,5-Fache an Leben kaufte also 1,6 Minuten. Der Grund ist,
 * dass der Portalschaden je Minute im gleichen Mass mitwaechst (1,6 auf 7,8):
 * Die Wirtschaft waechst exponentiell, und ein groesserer Puffer ist in gut
 * einer Minute weggefressen. Solange das so ist, laesst sich die Matchdauer
 * hier nicht einstellen.
 *
 * 40 statt 30, weil es nebenbei die gleichmaessigere Hero-Balance ergibt: Bei
 * sonst gleichem Stand und praktisch gleicher Dauer betraegt die Spanne
 * zwischen bestem und schlechtestem Hero 8,9 statt 14,4 Punkte. Warum das
 * Portal-Leben darauf wirkt, ist nicht geklaert - gemessen ist es.
 */
/*
 * 100 Leben, und dabei bleibt es (Produktentscheidung vom 30.09.2026).
 *
 * Das ist eine FESTLEGUNG, kein Messergebnis: Das Portalleben ist ab jetzt
 * kein Regler mehr. Wer die Matchdauer aendern will, aendert etwas anderes.
 *
 * Vorgeschichte, damit niemand denselben Weg noch einmal geht: Groessere
 * Wellen (30 Sekunden Abklingzeit, halbierte Creeppreise) brachten so viel
 * Druck, dass Matches auf 7,6 Minuten fielen. Gemessen half mehr Portalleben
 * durchaus -
 *
 *   Portalleben   Dauer
 *   100            7,6 Min
 *   150           11,6 Min
 *   220           13,4 Min
 *   340           13,5 Min
 *
 * - aber es war ein Pflaster. Die Ursache liegt woanders: Die Verteidigung
 * zerstoerte 13.161 Creepleben je Minute, der Angriff schickte 24.604. Der
 * Ueberschuss lief einfach durch, und das Portal musste ihn schlucken. Bei 340
 * Leben hielt die Verteidigung nur noch 53 Prozent der Creeps auf statt 79 -
 * dann fragt man sich zu Recht, wofuer man das Gold ausgegeben hat.
 *
 * Ein Balancewert, der nur einen Missstand zudeckt, verdeckt ihn auch vor uns.
 * Deshalb steht er jetzt fest, und die Matchdauer wird ueber die Kampfwerte
 * geregelt - dort, wo die Ursache sitzt.
 *
 * (Das Feld im Netzwerkschema ist seit dem Ausflug auf 340 ein uint16 statt
 * uint8. 100 passt auch in acht Bit, aber die Falle bleibt besser entschaerft:
 * Bei 340 kam beim Client 84 an, ohne Fehlermeldung. Der Wachtest dazu steht
 * in packages/shared/src/state/feldbreiten.test.ts.)
 */
export const PORTAL_MAX_HP = 100;

/*
 * Das Portal regeneriert nicht und verteidigt sich nicht selbst.
 *
 * Das steht hier als Kommentar und nicht als Konstante: Es gibt keinen Code,
 * der eine Portalregeneration kennt. Eine Konstante PORTAL_REGENERATES =
 * false sah aus wie ein Regler, den man umlegen kann - umgelegt haette sie
 * nichts bewirkt (01.10.2026).
 */

/**
 * Vorbereitungszeit vor dem Countdown, in Sekunden.
 *
 * Produktentscheidung vom 24.09.2026: von 20 auf 10 gesenkt. Zwanzig Sekunden
 * Warten vor jedem Match waren im Test schlicht zu lang - kaufen kann man in
 * dieser Zeit ohnehin nichts.
 */
export const PREPARATION_SECONDS = 10;

/** Countdown "3 - 2 - 1 - FIGHT" in Sekunden. */
export const COUNTDOWN_SECONDS = 3;

/** Zeit zwischen SEND und dem Spawn der Welle beim Gegner, in Sekunden. */
export const WAVE_TRANSFER_SECONDS = 0;

/**
 * Wie lange ein gefallener Hero noch an Ort und Stelle liegen bleibt
 * (Produktentscheidung vom 25.09.2026, Dauer geaendert am 24.10.2026).
 *
 * Danach verschwindet er, die Kamera springt zum Portal, und dort wartet er
 * seine Respawnzeit ab. Vorher lag er die ganze Respawnzeit ueber mitten im
 * Gefecht - das sah aus, als waere er noch im Spiel, und die Kamera klebte
 * an einer Stelle, an der es fuer ihn nichts mehr zu tun gab.
 *
 * Am 24.10.2026 von 1,5 auf 5 Sekunden erhoeht - und die Zeit zaehlt nicht
 * mehr zur Respawnzeit, sondern kommt davor (Produktentscheidung). Der Tod
 * ist damit absichtlich teurer: Wer frueh stirbt, ist 9 statt 4 Sekunden aus
 * dem Spiel, spaet 17 statt 12. Die Wartezeit AM PORTAL ist unveraendert -
 * das ist weiter HERO_RESPAWN_SCHEDULE.
 *
 * TUNING_PLACEHOLDER.
 */
export const HERO_DEATH_FADE_SECONDS = 5;

/**
 * Formation einer gesendeten Welle (Masterprompt Abschnitt 72).
 *
 * Eine Welle erscheint Reihe fuer Reihe: erst FRONT, dann CENTER, dann BACK.
 * Innerhalb einer Reihe stehen die Creeps in Gliedern nebeneinander - so breit,
 * wie WAVE_RANK_WIDTH erlaubt. Ist eine Reihe laenger, folgt ein weiteres Glied
 * dahinter.
 *
 * Alle Werte hier sind TUNING_PLACEHOLDER.
 */

/** Zeitabstand zwischen zwei Gliedern derselben Reihe, in Sekunden. */
export const WAVE_SPAWN_SPACING_SECONDS = 0.6;

/** Zusaetzlicher Abstand zwischen zwei Reihen (Front/Center/Back), in Sekunden. */
export const WAVE_ROW_GAP_SECONDS = 0.6;

/** Hoechstens so viele Creeps stehen in einem Glied nebeneinander. */
export const WAVE_RANK_WIDTH = 4;

/**
 * Wie weit die Formation seitlich reicht - als Anteil der halben Lanebreite.
 * 0.5 heisst: Die aeusseren Creeps stehen auf halbem Weg zum Rand.
 */
export const WAVE_LATERAL_SPREAD = 0.5;

/** Anzahl vergangener Wellen, die in der History vorgehalten werden. */
export const WAVE_HISTORY_SIZE = 3;

/**
 * Wie viel vom Schaden eines Heroes bei einem anderen HERO ankommt
 * (Produktentscheidung vom 07.10.2026, GAME_DESIGN Abschnitt 5b).
 *
 * Hero gegen Hero ist ausdruecklich NICHT der Siegweg. Der Abschlag steht
 * deshalb hier in den Daten und nicht verstreut in den Skills: Es gibt
 * genau eine Stelle, an der er wirkt (applyHeroDamage), und genau eine,
 * an der er sich aendern laesst.
 *
 * VERBINDLICH aus dem Masterprompt.
 */
export const PVP_DAMAGE_MULTIPLIER = 0.4;

/**
 * Wie lange Betaeubung und Verlangsamung bei einem HERO wirken
 * (Produktentscheidung vom 07.10.2026, GAME_DESIGN Abschnitt 5b).
 *
 * Dieselbe Begruendung wie beim Schaden - und derselbe Grund, warum der
 * Wert in den Daten steht: Ein Hero, der durch zwei Skills dauerhaft
 * steht, waere das Ende jeder Entscheidung.
 *
 * VERBINDLICH aus dem Masterprompt.
 */
export const PVP_CC_DURATION_MULTIPLIER = 0.5;

/**
 * Wie lange der Hero nach einem Treffer auf seinen Angreifer aus ist.
 *
 * Produktentscheidung vom 23.09.2026: Wer den Hero angreift, bekommt
 * automatisch seine Aufmerksamkeit - er schlaegt zurueck, sobald der Angreifer
 * in Reichweite ist. Eine eigene Auswahl des Spielers hat Vorrang.
 */
export const HERO_RETALIATION_SECONDS = 6;

/**
 * Respawnzeit des Heroes in Sekunden, gestaffelt nach Matchzeit.
 * fromMinute gilt einschliesslich, bis zur naechsten Stufe.
 */
export const HERO_RESPAWN_SCHEDULE: readonly { fromMinute: number; seconds: number }[] = [
  { fromMinute: 0, seconds: 4 },
  { fromMinute: 3, seconds: 6 },
  { fromMinute: 6, seconds: 8 },
  { fromMinute: 9, seconds: 10 },
  { fromMinute: 12, seconds: 12 },
];

/** Obergrenze der Respawnzeit in Sekunden. */
export const HERO_RESPAWN_MAX_SECONDS = 12;

/** Matchphasen nach Minute (Masterprompt Abschnitt 87). */
/**
 * Countdown im Warteraum, nachdem beide bereit sind
 * (Produktentscheidung vom 05.10.2026). TUNING_PLACEHOLDER.
 *
 * Kurz gehalten: Danach folgen ohnehin 20 Sekunden Vorbereitung im Spiel.
 * Dieser Countdown ist nur der gemeinsame Moment "jetzt geht es los".
 */
/*
 * FUENF SEIT DEM 13.10.2026 (vorher drei, auf Robins Wort).
 *
 * Die Zeit hat zwei Aufgaben: Sie gibt beiden einen Moment zum Ankommen -
 * und sie ist der Puffer, in dem die Spielwelt fertig geladen sein muss.
 * Seit die Welt schon im Warteraum entsteht, ist das meist laengst
 * erledigt; die zwei Sekunden mehr sind die Reserve fuer ein langsames
 * Handy. TUNING_PLACEHOLDER.
 */
export const LOBBY_COUNTDOWN_SECONDS = 5;

/**
 * Wie lange die Lobby eines SCHNELLMATCHES laeuft, bevor es von selbst
 * losgeht (Produktentscheidung vom 13.10.2026).
 *
 * Eine Minute: Zeit, den Linebound zu waehlen und anzukommen - aber nicht
 * so viel, dass man sich fragt, ob der andere noch da ist. Wer frueher
 * will, drueckt Ready; beide Ready ueberspringt die Uhr.
 *
 * NUR IM SCHNELLMATCH. In einer privaten Lobby (Create/Join) gibt es keine
 * Uhr: Dort hat man sich verabredet und bespricht vielleicht noch etwas.
 *
 * Damit kippt die Entscheidung vom 05.10.2026, die ausdruecklich KEINE
 * Zeitgrenze wollte - der Grund damals: Matches starteten gegen Leute, die
 * nicht am Geraet sassen. Mit einer Minute statt eines sofortigen Starts
 * ist das klein, und im Schnellmatch kennt man einander nicht und will
 * spielen. TUNING_PLACEHOLDER.
 */
export const LOBBY_WAIT_SECONDS = 60;

export const MATCH_PHASE_SCHEDULE: readonly { fromMinute: number; phase: MatchPhase }[] = [
  { fromMinute: 0, phase: 'early' },
  { fromMinute: 3, phase: 'mid1' },
  { fromMinute: 6, phase: 'mid2' },
  { fromMinute: 9, phase: 'endgame' },
  { fromMinute: 12, phase: 'overtime' },
];

/** Minute, ab der Overtime beginnt. Nur der Phasenname - der Druck steigt frueher. */
export const OVERTIME_START_MINUTE = 12;

/* ======================================================================
 * Wellendruck (Produktentscheidung vom 28.09.2026)
 *
 * Neu erscheinende Angriffscreeps werden mit der Zeit staerker. Ohne das
 * endet ein Match zwischen zwei gleich starken Spielern gar nicht: Gemessen
 * ueber 720 Matches erreichten nur 5,3 Prozent aller gesendeten Creeps das
 * Portal, und 61 Prozent der Matches liefen in den Abbruch.
 *
 * Die Regel stand vorher schon in den Daten - aber mit Beginn in Minute 13
 * und, viel wichtiger, ohne dass die Simulation sie je angewendet haette.
 *
 * Ab Mid II, nicht erst ab Overtime: Der Druck soll das Zielfenster von 10
 * bis 15 Minuten aufbauen und nicht danach einsetzen.
 * ====================================================================== */

/** Minute, ab der der Wellendruck zu steigen beginnt. */
export const WAVE_PRESSURE_START_MINUTE = 6;

/** Erste Minute, in der ein Druckbonus vergeben wird. */
export const WAVE_PRESSURE_FIRST_BONUS_MINUTE = 7;

/** Abstand zwischen zwei Druckstufen in Sekunden. */
export const WAVE_PRESSURE_STEP_SECONDS = 60;

/**
 * Bonus je Druckstufe auf Max HP und Schaden NEU erscheinender Angriffscreeps.
 *
 * TUNING_PLACEHOLDER - ausgemessen mit dem Massentest.
 *
 * 4 -> 6 Prozent (Produktentscheidung vom 05.10.2026).
 *
 * Am selben Tag wurden die Helden um die Haelfte gestaerkt und das
 * Earlygame gestreckt. Beides zusammen liess die Lane mehr schlucken: Die
 * Spieler verschickten 31 Prozent MEHR Creeps und richteten damit WENIGER
 * Portalschaden an (71,9 -> 68,9 von 100). Die Matches wuchsen von 12,9 auf
 * 16,6 Minuten, und 12 Prozent liefen in den Abbruch.
 *
 * Der Druck war fuer ein Zielfenster von 10 bis 15 Minuten gerechnet - dort
 * sind wir nicht mehr. Erhoeht wird er und nicht etwas anderes, weil er als
 * einziger Hebel die ersten sieben Minuten per Konstruktion nicht beruehrt:
 * Er setzt erst ab WAVE_PRESSURE_FIRST_BONUS_MINUTE ein, das gestreckte
 * Earlygame bleibt also unangetastet.
 *
 * Zum Vergleich, was ein Creep an Leben und Schaden hat:
 *
 *   Minute   10     12     15     16     20
 *   bei 4 %  116 %  124 %  136 %  140 %  156 %
 *   bei 6 %  124 %  136 %  154 %  160 %  184 %
 */
export const WAVE_PRESSURE_PER_STEP = 0.06;

/*
 * Der Portalschaden bleibt fest (Produktentscheidung vom 28.09.2026).
 *
 * Ein Durchbruch zaehlt immer gleich viel. Der Anstieg kommt allein daher,
 * dass mehr Creeps es ueberhaupt bis zum Portal schaffen.
 *
 * Auch das ist ein Kommentar und keine Konstante: Der Wellendruck in
 * match-time.ts wirkt auf Leben und Schaden neuer Creeps, den Portalschaden
 * fasst er gar nicht an. Ein Schalter dafuer haette nichts geschaltet
 * (01.10.2026).
 */

/** Regeneration des Heroes (Masterprompt Abschnitt 51). */
export const HERO_REGEN = {
  /** Sekunden ohne erhaltenen Schaden, bis die HP-Regeneration startet. */
  hpDelaySeconds: 5,
  /** Anteil der maximalen HP, der danach pro Sekunde regeneriert wird. */
  hpPerSecondRatio: 0.01,
} as const;

/**
 * Wie lange das Betreten des Sanctuary dauert
 * (GAME_DESIGN Abschnitt 5f, SANCTUARY_ENTRY_CHANNEL).
 *
 * Waehrend dieser Zeit bleibt der Hero verwundbar, Schaden bricht ab, und
 * eigene Angriffe brechen ebenfalls ab. Genau dadurch ist das Sanctuary
 * KEIN Sofort-Fluchtknopf: Wer verfolgt wird, kommt nicht mehr hinein.
 * TUNING_PLACEHOLDER.
 */
export const SANCTUARY_ENTRY_SECONDS = 3;

/**
 * Wie lange das Verlassen dauert (SANCTUARY_EXIT_TIME).
 *
 * Kuerzer als das Betreten: Hinein ist die Entscheidung, hinaus nur die
 * Rueckkehr. Trotzdem nicht null - sonst koennte man im Sanctuary stehen
 * und im Moment der Gelegenheit herausspringen. TUNING_PLACEHOLDER.
 */
export const SANCTUARY_EXIT_SECONDS = 2;

/**
 * Um wie viel schneller Leben im Sanctuary zurueckkommt.
 *
 * "Spuerbar schneller als im Feld - aber keine Sofortheilung."
 * Mit Faktor 9,75 und HERO_REGEN.hpPerSecondRatio sind das 9,75 Prozent
 * der maximalen Leben je Sekunde: Von halb tot auf voll dauert damit gut
 * fuenf Sekunden - plus drei Sekunden hinein, zwei hinaus und zweimal den
 * Weg.
 *
 * ZWEIMAL ERHOEHT, BEIDE MALE NACH DEM SPIELGEFUEHL:
 *   6 -> 7,5   (16.10.2026, +25 %) "dauert zu lange"
 *   7,5 -> 9,75 (17.10.2026, +30 %) "dauert mir noch zu lang"
 *
 * Der Weg dorthin und zurueck bleibt unveraendert - der Preis ist also
 * derselbe, nur der Aufenthalt wird kuerzer. TUNING_PLACEHOLDER.
 */
export const SANCTUARY_HP_REGEN_FACTOR = 9.75;

/**
 * Dasselbe fuer Mana und Energy.
 *
 * Niedriger als beim Leben: Die Ressource kommt auch im Feld zurueck,
 * das Leben praktisch nicht.
 *
 * 3 -> 3,75 -> 4,875 (16. und 17.10.2026) - dieselben Schritte wie beim
 * Leben. Beides zusammen entscheidet, wie lange man drinnen steht; haette
 * nur eines davon zugelegt, waere die Wartezeit die des anderen
 * geblieben. TUNING_PLACEHOLDER.
 */
export const SANCTUARY_RESOURCE_REGEN_FACTOR = 4.875;

/*
 * Die maximale Pausendauer stand hier einmal mit 30 Sekunden
 * (Masterprompt Abschnitt 93) - unbenutzt, waehrend das Spiel mit 15
 * Sekunden lief. Zwei Konstanten mit demselben Namen und verschiedenen
 * Werten: Wer die falsche las, dokumentierte etwas, das es nicht gab.
 *
 * Entfernt am 06.10.2026. Der geltende Wert und die Begruendung fuer die
 * Abweichung stehen jetzt an einer einzigen Stelle:
 * DISCONNECT_PAUSE_SECONDS in packages/shared/src/constants.ts.
 */

/*
 * Hier stand DISCONNECT_PAUSES_PER_PLAYER = 1: "hoechstens eine Pause je
 * Spieler und Match" aus Masterprompt Abschnitt 93.
 *
 * Entfernt am 06.10.2026, nachdem entschieden war, dass es diese Regel
 * NICHT geben soll. Es zaehlt allein die Zeit - dreimal fuenf Sekunden sind
 * erlaubt und verbrauchen dasselbe wie einmal fuenfzehn.
 *
 * Die Begruendung steht bei DISCONNECT_PAUSE_SECONDS in
 * packages/shared/src/constants.ts, wo auch der geltende Wert steht. Dieser
 * Hinweis bleibt hier, damit die Regel nicht aus dem Masterprompt heraus
 * noch einmal eingebaut wird.
 */

/**
 * Wie weit zwei Einheiten auseinanderstehen duerfen und noch als EINE
 * Frontline gelten (GAME_DESIGN Abschnitt 5a).
 *
 * Grob zwei Creep-Laengen plus die seitliche Streuung beim Erscheinen: Wer
 * weiter zurueckliegt, gehoert sichtbar nicht mehr zum selben Pulk.
 *
 * Zu klein, und die Linie zerfaellt in lauter Einzelgruppen und springt. Zu
 * gross, und ein weit vorausgelaufener Ausreisser zieht sie mit.
 * TUNING_PLACEHOLDER.
 */
export const FRONTLINE_CLUSTER_GAP = 14;

/**
 * So viele Einheiten machen einen Pulk aus. Weniger gilt als Ausreisser.
 *
 * Drei und nicht zwei: Zwei vorausgelaufene Creeps sind noch keine
 * Raumkontrolle, und die Regel lautet ausdruecklich, dass ein einzelner
 * Durchbruch dem Hero keinen Raum verschaffen darf. TUNING_PLACEHOLDER.
 */
export const FRONTLINE_CLUSTER_MIN_UNITS = 3;

/**
 * Wie schnell die angezeigte Frontline der errechneten folgt, in Einheiten
 * je Sekunde.
 *
 * Etwas schneller als der schnellste kaempfende Creep: Beim Vorruecken haengt
 * sie nicht hinterher, beim Tod des Vordersten federt sie den Ruecksprung ab.
 * TUNING_PLACEHOLDER.
 */
export const FRONTLINE_FOLLOW_SPEED = 12;

/**
 * Ab welchem Anteil der Lane die eigene Linie als zurueckgedraengt gilt
 * (GAME_DESIGN Abschnitt 13, Produktentscheidung vom 07.10.2026).
 *
 * Gemessen daran, wie viel Weg die gegnerische Armee schon hinter sich hat.
 * Zwei Drittel heisst: Sie steht tief in der eigenen Haelfte - dann bedeutet
 * die Zahl unmittelbar "ich werde zurueckgedraengt".
 *
 * Die Warnung tritt NEBEN den dauerhaften Frontbalken, sie ersetzt ihn nicht:
 * Eine Anzeige, die immer da ist, wird im Gefecht uebersehen.
 * TUNING_PLACEHOLDER.
 */
export const FRONTLINE_PRESSURE_SHARE = 2 / 3;

/**
 * Wie lange ein Treffer am eigenen Anchor als Warnung nachwirkt.
 *
 * Sechs Sekunden, weil die Warnung zwei Dinge gleichzeitig schafft: Sie
 * ueberlebt den Blick auf den Shop, und sie ist wieder weg, bevor der
 * naechste Treffer faellt. TUNING_PLACEHOLDER.
 */
export const ANCHOR_HIT_WARNING_MS = 6000;

/**
 * Wie lange die Druckwarnung im HUD zu sehen ist, in Millisekunden.
 *
 * Sie BLITZT AUF und bleibt nicht stehen (Rueckmeldung aus dem Spiel vom
 * 12.10.2026). Vorher stand sie da, solange die Lage kritisch war - und
 * genau dann steht sie am laengsten: Wer zurueckgedraengt wird, hat die
 * Meldung minutenlang im Bild, bis sie zur Tapete wird.
 *
 * Wichtig ist der Unterschied zu ANCHOR_HIT_WARNING_MS darueber: Das ist
 * die Dauer eines GRUNDES - wie lange ein Treffer nachwirkt. Dies hier ist
 * die Dauer der ANZEIGE. Der Grund kann weiterbestehen, wenn die Anzeige
 * laengst wieder weg ist; der Bot zum Beispiel reagiert weiter darauf.
 * TUNING_PLACEHOLDER.
 */
export const FRONT_PRESSURE_WARNING_MS = 5000;

/**
 * Wie tief die Kampfzone um die eigene Frontline reicht - nach JEDER Seite
 * (GAME_DESIGN Abschnitt 5a, "Die Leash").
 *
 * Das wichtigste Unterscheidungsmerkmal zu einem MOBA: Die Army kontrolliert
 * den Raum, in dem der Hero kaempfen darf - nicht umgekehrt. Rueckt sie vor,
 * darf er weiter; verliert sie Boden, wandert sein Spielraum mit zurueck.
 *
 * Die Zone liegt MITTIG auf der Frontline: So weit davor UND so weit
 * dahinter. Aufgehalten wird er an der Vorderkante.
 *
 * Bis zum 08.10.2026 hiess der Wert HERO_FRONTLINE_LEASH_DISTANCE und zog
 * die Grenze HINTER die Frontline. Im Spiel aufgefallen: Ein Nahkaempfer
 * stand damit hinter seinen eigenen Leuten und kam nie an den Kampf heran.
 * Der Name ist mitgewandert - eine Konstante, die gleich heisst und das
 * Gegenteil bedeutet, ist eine Falle.
 *
 * Produktentscheidung vom 08.10.2026: 20 statt 10.
 *
 * Mit 10 kam der Hero an die vorderste Reihe des Gegners - und keinen
 * Schritt weiter. Im Spiel aufgefallen: "ich konnte als held nicht bewusst
 * die backline angreifen." Genau das ist aber die Entscheidung, um die es
 * geht: Gehe ich auf die Heiler und Schuetzen los und stehe dafuer im
 * Pulk, oder bleibe ich hinten?
 *
 * Die 20 sind aus der Formation abgeleitet, nicht geraten: Eine Welle
 * erscheint Reihe fuer Reihe im Abstand von
 * WAVE_SPAWN_SPACING_SECONDS + WAVE_ROW_GAP_SECONDS. Bei Creep-Tempo 8
 * liegen zwischen FRONT und BACK damit rund 19 Einheiten. Wer die
 * hintere Reihe erreichen soll, braucht ungefaehr so viel Raum vor der
 * eigenen Front.
 *
 * Zum Einordnen: Ein Hero laeuft 20 Einheiten in gut einer Sekunde,
 * ein Creep-Pulk ist rund 14 Einheiten tief, die Lane ist 224 lang.
 * TUNING_PLACEHOLDER.
 */
export const HERO_FRONTLINE_ZONE_DEPTH = 20;

/**
 * Wie weit er vor sein eigenes Anchor darf, wenn er GAR KEINE Armee
 * draussen hat (Produktentscheidung vom 08.10.2026).
 *
 * Ohne diese Grundstrecke klebte der Hero in jeder Pause zwischen zwei
 * Wellen an seinem Anchor fest. Sie ist bewusst knapp: Wer nichts schickt,
 * bekommt etwas Luft zum Verteidigen, aber keinen Raum.
 *
 * Sie gilt auch als UNTERGRENZE, wenn die eigene Armee weit zurueckgedraengt
 * ist - sonst wuerde ein zusammengebrochener Pulk den Hero noch enger
 * stellen als gar keiner. TUNING_PLACEHOLDER.
 */
export const HERO_LEASH_BASE_DISTANCE = 40;

/**
 * Auf welcher Strecke vor der Vorderkante der Hero langsamer wird.
 *
 * Die Grenze darf sich nicht wie eine unsichtbare Wand anfuehlen
 * (GAME_DESIGN Abschnitt 5a). Auf diesen letzten Metern faellt sein Tempo
 * NACH VORNE auf null; zur Seite und nach hinten bleibt er voll schnell.
 *
 * Ein Anteil der Zone und keine eigene Zahl: Wird die Zone groesser,
 * waechst das Polster mit, bleibt aber ein Polster. Mit der vollen
 * Zonentiefe (so war es kurz) kroche der Hero die ganze vordere Haelfte
 * entlang - das haette genau das kaputtgemacht, wofuer die groessere Zone
 * da ist: hinein in die gegnerische Backline.
 *
 * SEIT DEM 12.10.2026 LIEGT DIESES POLSTER HINTER DER LINIE, nicht davor
 * (Rueckmeldung aus dem Spiel). Der Hero laeuft also in den Rauch hinein,
 * wird dabei immer langsamer und bleibt nach dieser Strecke stehen.
 *
 * Vorher bremste er VOR der Linie ab und stand dann an ihr wie an einer
 * Wand. Jetzt ist die Kante weich: Man sieht und spuert, dass es zu Ende
 * geht, statt dagegen zu laufen. Die Linie im Bild bleibt dieselbe - sie
 * ist nur nicht mehr das Ende, sondern der Anfang vom Ende.
 * TUNING_PLACEHOLDER.
 */
export const HERO_LEASH_BRAKE_ZONE = HERO_FRONTLINE_ZONE_DEPTH * 0.3;

/**
 * Wie schnell die Leash-Grenze nach VORNE gleitet, in Einheiten je Sekunde
 * (Rueckmeldung aus dem Spiel vom 12.10.2026).
 *
 * Vorher sprang sie: Oeffnet das Retaliation-Fenster, stand sie im selben
 * Takt eine Zonentiefe weiter vorn. "Das springt jetzt so weg von mir."
 *
 * Bewusst DEUTLICH schneller als ein Hero (15 bis 17): Sie soll sichtbar
 * wegziehen, aber nie im Weg stehen. Wer sie einholen koennte, haette im
 * Fenster eine Grenze statt der Freiheit, die es verspricht.
 * TUNING_PLACEHOLDER.
 */
export const HERO_LEASH_ADVANCE_SPEED = 40;

/**
 * Wie schnell die Leash-Grenze ZURUECK kommt, in Einheiten je Sekunde
 * (Rueckmeldung aus dem Spiel vom 10.10.2026).
 *
 * Vorher klebte sie beim Rueckzug starr am Hero und sprang damit genau
 * so schnell zurueck, wie er lief - im Bild wirkte der Nebel wie an den
 * Fersen festgenaeht. Jetzt hat er ein eigenes Tempo: Der Hero laeuft
 * mit 15 bis 17 Einheiten je Sekunde, der Nebel mit vier. Er kann ihm
 * also davonlaufen, und der Nebel kommt gemaechlich nach.
 *
 * VIER SEIT DEM 12.10.2026 (vorher acht, auf Robins Wort zum Ausprobieren).
 * Das heisst: Eine volle Zonentiefe braucht fuenf Sekunden, die Strecke
 * eines Retaliation-Fensters sechs.
 *
 * Je kleiner die Zahl, desto laenger behaelt man erkaempften Raum ohne
 * Armee - der Raum verfaellt erst, wenn der Nebel wieder beim Hero ist.
 * Unter etwa drei wuerde das Retaliation-Fenster dadurch deutlich mehr
 * wert, als es gedacht ist.
 *
 * Nur fuer den Rueckweg. Nach vorne gilt HERO_LEASH_ADVANCE_SPEED fuer
 * die angezeigte Grenze; das Gedaechtnis schiebt die Armee, und die hat
 * ihre eigene Daempfung (ziehFrontlineNach). TUNING_PLACEHOLDER.
 */
export const HERO_LEASH_RETREAT_SPEED = 4;

/**
 * Wie nah zwei Einheiten einander kommen duerfen, von Mitte zu Mitte
 * (Produktentscheidung vom 13.10.2026).
 *
 * Bis dahin gab es gar keine Kollision: Zehn Creeps konnten deckungsgleich
 * auf demselben Punkt stehen. Jetzt blockieren sie einander - wer in einen
 * anderen hineinlaufen wuerde, kommt nicht durch.
 *
 * 1,4 SEIT DEM 13.10.2026 (vorher 1). Mit eins standen sie zwar nicht mehr
 * deckungsgleich, aber eine Reihe liess sich noch fast muehelos durchqueren.
 * Robin: "ein bisschen mehr Kollision, dass es sich gefuehlt schwerer
 * anfuehlt durchzukommen."
 *
 * Sie duerfen sich weiter ueberlappen - es geht nicht um eine Aufstellung,
 * sondern um Widerstand.
 *
 * DIE OBERGRENZE IST DIE ANGRIFFSREICHWEITE. Ein Nahkaempfer schlaegt auf
 * zwei Einheiten weit zu. Waere der Mindestabstand ebenso gross, stuenden
 * sich zwei Armeen gegenueber, ohne sich zu erreichen - der Kampf stuende
 * still. Ein Test haelt diesen Zusammenhang fest, damit die Zahl nicht
 * eines Tages unbemerkt darueber waechst. TUNING_PLACEHOLDER.
 */
export const UNIT_SEPARATION_RADIUS = 1.4;

/**
 * Wie schnell zwei Einheiten auseinanderrutschen, die bereits ineinander
 * stehen, in Einheiten je Sekunde.
 *
 * Das Blockieren allein genuegt nicht: Wer schon ineinander steht - weil er
 * gerade erschienen ist oder beschworen wurde - kaeme nie wieder heraus,
 * weil jede Bewegung nur verhindert und nichts behoben wird.
 *
 * Bewusst sehr langsam ("ganz ganz leicht wegschieben", Robin am
 * 13.10.2026): Es soll aussehen, als machten sie Platz - nicht, als stiessen
 * sie sich ab. TUNING_PLACEHOLDER.
 */
export const UNIT_SEPARATION_PUSH_SPEED = 1.5;

/**
 * Wie schnell ein Creep zur Seite gibt, in den der Hero hineinlaeuft, in
 * Einheiten je Sekunde (Produktentscheidung vom 25.10.2026).
 *
 * Seit dem 13.10.2026 blockierten Creeps den Hero vollstaendig: Wer hinter
 * einer Reihe stand, kam nicht mehr vorbei. Robin: "Ich will keinen
 * vollkommen hard block der units haben ... sie sollen den Weg nicht 100
 * Prozent blockieren und sich ganz leicht verschieben koennen."
 *
 * Der Masterprompt (Abschnitt 33) verbietet ohnehin, dass eine Reihe Creeps
 * den Hero dauerhaft bodyblockt - mit dem Draengen gilt das wieder.
 *
 * Bewusst langsam: Er muss sich hineinlehnen, und es dauert einen Moment.
 * Zum Vergleich laeuft ein Hero rund 15 Einheiten je Sekunde - der Creep
 * gibt also mit etwa einem Siebtel davon zur Seite. TUNING_PLACEHOLDER.
 */
export const HERO_PUSH_ASIDE_SPEED = 2;

/**
 * Wie lange der Hero gestanden haben muss, bevor er zuschlaegt, in Sekunden
 * (Produktentscheidung vom 25.10.2026).
 *
 * Robin: "Ein Angriff passiert nur, wenn man steht." Vorher kam die
 * Angriffsbewegung mitten im Laufen, und die Figur rutschte waehrend des
 * Schlages weiter.
 *
 * Warum eine Wartezeit und nicht nur "gerade jetzt keine Richtung
 * gedrueckt": Ohne sie genuegt ein Antippen zwischen zwei Schlaegen, und
 * man behaelt im Laufen fast seinen ganzen Schaden. Mit ihr entscheidet man
 * sich zwischen Laufen und Kaempfen - genau das war der Zweck.
 *
 * Gilt nur fuer den normalen Angriff. Faehigkeiten gehen weiter im Laufen
 * (Produktentscheidung vom 25.10.2026): Einige von ihnen SIND Bewegung.
 * TUNING_PLACEHOLDER.
 */
export const HERO_ATTACK_STAND_SECONDS = 0.3;

/**
 * Wie viel Luft nach dem Treffmoment einer Faehigkeit noch bindet, in
 * Sekunden (Produktentscheidung vom 25.10.2026, zweite Fassung).
 *
 * Robin: "Wenn man einen Skill macht, bleibt man bis zur Fertigstellung der
 * Skillanimation stehen." Zuerst stand hier eine einzige Zahl fuer alle
 * Faehigkeiten (0,4 Sekunden), weil der Server die Animationslaengen nicht
 * kennt. Seit die Grafiksitzung sie gemessen hat, steht der Treffmoment je
 * Faehigkeit in den Spieldaten (hitSeconds) - gebunden wird bis dahin plus
 * dieser Luft.
 *
 * WARUM NICHT BIS ZUM ENDE DER ANIMATION: Die Gesamtlaenge enthaelt ein
 * ruhiges Zurueckfinden in die Grundhaltung. Deadfall dauert so 1,7
 * Sekunden - so lange festzustehen ist im Gedraenge eine Ewigkeit. Der
 * Rest der Bewegung wird stattdessen abgebrochen, sobald man losgeht
 * (dieselbe Regel wie beim normalen Angriff).
 *
 * TUNING_PLACEHOLDER.
 */
export const HERO_SKILL_LOCK_LUFT_SECONDS = 0.2;

/**
 * Wie weit ein Creep seitlich ausholt, wenn ihm ein anderer den Weg zum
 * Ziel versperrt - im Bogenmass (Produktentscheidung vom 17.10.2026).
 *
 * Robin: "Mir ist aufgefallen, dass wenn die Creeps sich gegenseitig
 * blockieren, sie sich dahinter aufreihen. Die Creeps sollen ein bisschen
 * schlauer werden und dann um den Helden ggf. rumlaufen, um auch von
 * hinter ihm an ihn ran zu kommen."
 *
 * Seit es Kollision gibt (13.10.2026), steht der zweite Creep hinter dem
 * ersten und der dritte hinter dem zweiten: eine Schlange statt eines
 * Rings. Wer blockiert ist, zielt deshalb nicht mehr auf das Ziel selbst,
 * sondern auf einen Punkt daneben - er laeuft um das Ziel herum, bis seine
 * Gasse frei ist.
 *
 * 0,9 sind rund 50 Grad. Kleiner wuerde kaum ausweichen (der Creep bliebe
 * in der Schlange), deutlich groesser laesst ihn fast quer laufen, statt
 * sich dem Ziel zu naehern. TUNING_PLACEHOLDER.
 */
export const CREEP_UMLAUF_WINKEL = 0.9;

/*
 * HIER STANDEN DREI WERTE FUER EINE BREMSE (13.10.2026).
 *
 * Der Hero wurde im Gedraenge langsamer, statt aufgehalten zu werden.
 * Nach einem Versuch im Spiel hat Robin es umgedreht: Er wird jetzt
 * blockiert wie alle anderen auch, mit demselben UNIT_SEPARATION_RADIUS.
 * Damit braucht es keine eigenen Zahlen mehr.
 */

/**
 * Wie nah der Hero an einem sterbenden Gegner sein muss, um Gold und
 * Erfahrung dafuer zu bekommen (GAME_DESIGN Abschnitt 5c).
 *
 * Auf der gemeinsamen Lane toeten ueberwiegend Creeps andere Creeps. Ein
 * Last-Hit-System gibt es deshalb ausdruecklich nicht - belohnt wird
 * PRAESENZ AN DER FRONT. Wer weit weg steht oder tot ist, bekommt nichts.
 *
 * Die Zahl ist aus der Kampfzone abgeleitet und nicht geraten: So weit,
 * wie die ganze Zone tief ist (HERO_FRONTLINE_ZONE_DEPTH nach jeder
 * Seite). Wer irgendwo in seiner Kampfzone steht, ist damit normalerweise
 * dabei; wer sich zum Anchor zurueckzieht, nicht mehr.
 *
 * Gemessen wird zum STERBENDEN CREEP, nicht zur Frontline
 * (Produktentscheidung vom 09.10.2026): Sonst bekaeme ein Hero, der hinten
 * in seiner Zone parkt, dasselbe wie einer, der mitten im Pulk steht - und
 * die Entscheidung, nach vorn zu gehen, waere wieder umsonst.
 *
 * TUNING_PLACEHOLDER.
 */
export const FRONTLINE_REWARD_RANGE = HERO_FRONTLINE_ZONE_DEPTH * 2;

/*
 * Hier stand RETALIATION_EXTENSION_DISTANCE - entfernt am 12.10.2026.
 *
 * Sechs Einheiten, die der Hero waehrend des Retaliation Window ueber
 * seine Leash hinaus durfte. Die Zahl konnte ihren Zweck nicht erfuellen:
 * Das Fenster gibt es, damit ein Nahkaempfer einen Fernkaempfer erreicht,
 * der hinter seiner Front steht und ihn beschiesst - sechs Einheiten
 * reichen dafuer nie (im Spiel aufgefallen am 12.10.2026).
 *
 * Jetzt reicht das Fenster bis zum ANGREIFER und keinen Schritt weiter
 * (leash.ts). Dafuer gibt es keine Zahl mehr - und ein toter Tuningwert
 * waere schlimmer als keiner: Er stuende in WERTE.md, und jemand wuerde
 * daran drehen und sich wundern, dass nichts passiert.
 */

/**
 * Wie lange das Retaliation Window nach einem Treffer offen bleibt.
 *
 * Weitere Treffer erneuern die Dauer. Solange der Gegner also trifft, laeuft
 * das Fenster weiter - und genau so ist es gemeint: Wer beschossen wird,
 * darf nachsetzen.
 *
 * 3 -> 1,5 Sekunden (Produktentscheidung vom 12.10.2026). Waehrend des
 * Fensters haelt die Grenze nicht auf; begrenzt wird also durch die ZEIT.
 * Bei einem Lauftempo von rund 16 Einheiten je Sekunde sind das etwa 24
 * Einheiten je Treffer - ein Neuntel der Lane.
 *
 * NICHT ZU VERWECHSELN mit HERO_RETALIATION_SECONDS: Das ist die
 * Aufmerksamkeit des Heroes fuer einen CREEP, der ihn getroffen hat. Hier
 * geht es um das Recht, nach einem Treffer des gegnerischen HEROES ein
 * Stueck weiter vorzuruecken. TUNING_PLACEHOLDER.
 */
export const RETALIATION_WINDOW_SECONDS = 1.5;
